import React, {
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useState,
} from "react";
import { useAccount } from "@yuno/dashboard-container-mfe";
import { useGetCheckoutTemplate } from "@yuno/dashboard-api-mfe";
import { useThemeCustom } from "../hooks/use-custom-theme";
import { darkTheme } from "../theme/dark";
import { defaultTheme } from "../theme/default";
import { minimalistTheme } from "../theme/minimalist";
import { CustomTheme } from "../types/custom-theme";
import isEqual from "lodash/isEqual";
import { customTheme } from "../theme/customTheme";
import merge from "lodash/merge";
import { MainContext } from "./main-context";

export type ICustomTheme = {
  customTheme?: CustomTheme;
  handleChangeColor?: (path: string, value: string) => void;
  handleSelect?: (path: string, value: string) => void;
};

export const ThemeContext = React.createContext(null);

export const ThemeProvider = ({ children }) => {
  const [themeSelected, setThemeSelected] = useState("default");
  const [userAction, setUserAction] = useState(false);
  const [theme, setTheme] = useState({
    ...JSON.parse(JSON.stringify(defaultTheme)),
  });
  const account = useAccount();

  const {
    data: themeRequest,
    refetch: refetchCheckoutTemplate,
    isSuccess,
    isLoading,
    isFetching,
  } = useGetCheckoutTemplate({
    accountCode: account?.code,
  });

  useEffect(() => {
    if (account?.code) {
      refetchCheckoutTemplate({ accountCode: account?.code });
    }
  }, [account?.code]);

  const { setData: setDataTheme } = useThemeCustom({
    data: { ...theme },
    onChangeData: setTheme,
  });

  useEffect(() => {
    if (isSuccess && !themeRequest?.empty) {
      setThemeSelected("custom");
      setTheme({ ...themeRequest.template });
    }

    return () => {
      localStorage.removeItem("customTheme");
      setThemeSelected("default");
    };
  }, [isSuccess, isFetching, isLoading]);

  useEffect(() => {
    const customStored = localStorage.getItem("customTheme");

    if (themeSelected === "default") setTheme({ ...defaultTheme });
    if (themeSelected === "darkMode") setTheme({ ...darkTheme });
    if (themeSelected === "minimalist") setTheme({ ...minimalistTheme });

    if (themeSelected === "custom") {
      if (customStored) {
        setTheme(
          merge(JSON.parse(JSON.stringify(theme)), JSON.parse(customStored))
        );
      } else {
        setTheme({
          ...JSON.parse(JSON.stringify(theme)),
        });
      }
    }
  }, [themeSelected]);

  const value = useMemo(
    () => ({
      themeSelected,
      setThemeSelected,
      customTheme: theme,
      setDataTheme,
      userAction,
      setUserAction,
    }),
    [themeSelected, setThemeSelected, setDataTheme, theme]
  );

  return (
    <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>
  );
};
